import { motion, useReducedMotion } from "framer-motion";
import { Plug, BotMessageSquare, Network, BookOpen, ShieldCheck } from "lucide-react";
import type { BrandFeatureItem } from "./brand-features";
import { useTranslations } from "use-intl";

// Maps icon name string to the actual Lucide component.
const ICON_MAP = {
  Plug,
  BookOpen,
  BotMessageSquare,
  Network,
  ShieldCheck,
} as const;

interface BrandFeatureProps {
  feature: BrandFeatureItem;
  index?: number;
}

export function BrandFeature({ feature, index = 0 }: BrandFeatureProps) {
  const t = useTranslations();
  const reduceMotion = useReducedMotion();
  const Icon = ICON_MAP[feature.iconName];

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, x: -14 }}
      animate={reduceMotion ? undefined : { opacity: 1, x: 0 }}
      transition={{ duration: 0.28, delay: index * 0.07, ease: "easeOut" }}
      whileHover={reduceMotion ? undefined : { x: 4 }}
      className="group relative flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.055] p-3 shadow-[0_18px_50px_rgba(0,0,0,0.12)] backdrop-blur-sm"
    >
      <div
        className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border border-white/10 transition-colors group-hover:bg-white/[0.16]"
        style={{ background: "oklch(1 0 0 / 0.10)" }}
      >
        <Icon className="size-4 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-white">{t(feature.titleKey)}</p>
        <p
          className="mt-0.5 text-xs leading-relaxed"
          style={{ color: "oklch(1 0 0 / 0.65)" }}
        >
          {t(feature.descKey)}
        </p>
      </div>
    </motion.div>
  );
}
